import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Loader2, Pencil, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function MediaLibrary() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef();
  const replaceRef = useRef();

  const load = () => { api.get("/media").then((r) => setMedia(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const doUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Caricata e ottimizzata"); load(); }
    catch (err) { toast.error("Caricamento fallito"); } finally { setUploading(false); e.target.value = ""; }
  };

  const openEdit = (m) => { setEditing(m); setForm({ alt: m.alt || "", title: m.title || "", caption: m.caption || "" }); };
  const save = async () => {
    try { await api.put(`/media/${editing.id}`, form); toast.success("Salvato"); setEditing(null); load(); }
    catch (e) { toast.error("Errore"); }
  };
  const doReplace = async (e) => {
    const file = e.target.files?.[0]; if (!file || !editing) return;
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/media/${editing.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Immagine sostituita ovunque venga usata"); setEditing(null); load(); }
    catch (err) { toast.error("Sostituzione fallita"); } finally { e.target.value = ""; }
  };
  const remove = async (m) => { if (!window.confirm("Eliminare questa immagine dalla libreria?")) return; await api.delete(`/media/${m.id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-serif text-2xl text-bordeaux">Libreria immagini</h2>
          <p className="text-sm text-oak/60">Tutte le foto caricate. Ottimizzate automaticamente per il web.</p>
        </div>
        <input ref={uploadRef} type="file" accept="image/*,video/*,application/pdf" className="hidden" onChange={doUpload} data-testid="media-upload-input" />
        <Button onClick={() => uploadRef.current?.click()} disabled={uploading} className="bg-bordeaux hover:bg-bordeaux-dark text-cream" data-testid="media-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}Carica immagine
        </Button>
      </div>

      {loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-brass" /></div> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map((m) => (
            <div key={m.id} className="bg-white rounded-xl border border-brass/15 overflow-hidden" data-testid={`media-${m.id}`}>
              <img src={`${fileUrl(m.url)}?v=${m.updated_at || ""}`} alt={m.alt || m.title} className="w-full h-36 object-cover" loading="lazy" />
              <div className="p-2">
                <p className="text-xs text-oak/70 truncate">{m.title || "senza titolo"}</p>
                <p className="text-[10px] text-oak/40 truncate">ALT: {m.alt || "—"}</p>
                <div className="flex gap-1 mt-1">
                  <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={() => openEdit(m)} data-testid={`media-edit-${m.id}`}><Pencil className="w-3 h-3 mr-1" />Modifica</Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(m)} data-testid={`media-del-${m.id}`}><Trash2 className="w-3.5 h-3.5 text-bordeaux" /></Button>
                </div>
              </div>
            </div>
          ))}
          {media.length === 0 && <p className="col-span-full text-center text-oak/50 py-10">Nessuna immagine. Carica la prima.</p>}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md bg-cream">
          <DialogHeader><DialogTitle className="font-serif text-xl text-bordeaux">Dettagli immagine</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <img src={`${fileUrl(editing.url)}?v=${editing.updated_at || ""}`} alt={form.alt} className="w-full h-44 object-cover rounded-lg" />
              <div><Label className="text-xs uppercase tracking-wider text-oak/70">Titolo</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="media-form-title" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-oak/70">Testo alternativo (ALT · SEO/accessibilità)</Label><Input value={form.alt} onChange={(e) => setForm((f) => ({ ...f, alt: e.target.value }))} data-testid="media-form-alt" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-oak/70">Didascalia</Label><Textarea value={form.caption} onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))} data-testid="media-form-caption" /></div>
              <input ref={replaceRef} type="file" accept="image/*" className="hidden" onChange={doReplace} data-testid="media-replace-input" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => replaceRef.current?.click()} data-testid="media-replace-btn"><RefreshCw className="w-4 h-4 mr-1" />Sostituisci foto</Button>
                <Button onClick={save} className="flex-1 bg-bordeaux hover:bg-bordeaux-dark text-cream" data-testid="media-save">Salva</Button>
              </div>
              <p className="text-[11px] text-oak/50">Sostituendo la foto, si aggiornerà automaticamente in ogni sezione dove è utilizzata.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
